/**
 * Candidate job alerts (Bet 1) and the candidate engagement report — the
 * cross-service contract.
 *
 * Who owns what:
 *   - vhire-candidate   owns the alert tables (Neon migration 268):
 *                       candidate_job_alerts, candidate_job_alert_runs,
 *                       candidate_job_alert_deliveries,
 *                       candidate_job_alert_engagements. It serves alert CRUD,
 *                       one-click unsubscribe, click/apply recording, the digest
 *                       scheduler (its existing every-minute cron) and the
 *                       engagement report.
 *   - vhire-job         `listPublicJobs` accepts `postedAfter` (unix seconds,
 *                       strictly newer public-fact touch, not first publication). Eligibility is
 *                       unchanged, so a closed or ineligible job never reaches
 *                       a digest.
 *   - vhire-email       `sendJobAlertDigest` renders one digest and makes ONE
 *                       Mailgun attempt. Delivery is at-most-once.
 *   - vhire-gateway     routes only; owns no DB.
 *   - v4-frontend       /me/alerts, the create dialog on /jobs,
 *                       /alerts/unsubscribe/[token], click/apply attribution,
 *                       /platform-admin/candidate-engagement.
 *
 * Consent: each alert is an explicit, candidate-initiated opt-in. The evidence
 * (copy version, time, IP, user agent) lives ON THE ALERT ROW. It is never
 * written to candidate_consent_events: the newest row there decides
 * marketplace visibility, so an alert opt-in or unsubscribe written there
 * would silently flip a candidate's marketplace consent
 * (kb: candidate--job-alert-consent-is-not-marketplace-consent).
 *
 * Plan: docs/plans/JOB_ALERTS_AND_ENGAGEMENT_PLAN_2026-09-28.md
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Vocabularies (migration 268 CHECK constraints mirror these exactly)
// ---------------------------------------------------------------------------

export const JOB_ALERT_FREQUENCIES = ['daily', 'weekly'] as const;
export type JobAlertFrequency = (typeof JOB_ALERT_FREQUENCIES)[number];

export const JOB_ALERT_STATUSES = ['active', 'paused', 'unsubscribed', 'deleted'] as const;
export type JobAlertStatus = (typeof JOB_ALERT_STATUSES)[number];

/** Where the candidate created the alert — the adoption breakdown on the engagement report. */
export const JOB_ALERT_SOURCES = ['jobs_search', 'dashboard_suggestion', 'manage_page'] as const;
export type JobAlertSource = (typeof JOB_ALERT_SOURCES)[number];

/** Why an alert is paused: the candidate chose to, or the address stopped accepting mail. */
export const JOB_ALERT_PAUSE_REASONS = ['candidate', 'delivery_failed'] as const;
export type JobAlertPauseReason = (typeof JOB_ALERT_PAUSE_REASONS)[number];

export const JOB_ALERT_UNSUBSCRIBE_VIAS = ['email_link', 'one_click', 'manage_page'] as const;
export type JobAlertUnsubscribeVia = (typeof JOB_ALERT_UNSUBSCRIBE_VIAS)[number];

/**
 * One row per digest email.
 *   sending   → transport attempt in flight (deliveries already recorded)
 *   sent      → provider accepted it
 *   uncertain → may or may not have been accepted; never re-sent
 *   failed    → definitively not delivered
 *   blocked   → no transport attempt was made (kill switch, misconfiguration);
 *               its deliveries are released so those jobs can go out later
 * A `sending` run whose lease expires becomes `uncertain`, never retried.
 */
export const JOB_ALERT_RUN_STATUSES = ['sending', 'sent', 'uncertain', 'failed', 'blocked'] as const;
export type JobAlertRunStatus = (typeof JOB_ALERT_RUN_STATUSES)[number];

export const JOB_ALERT_RUN_TRIGGERS = ['schedule', 'admin_test'] as const;
export type JobAlertRunTrigger = (typeof JOB_ALERT_RUN_TRIGGERS)[number];

export const JOB_ALERT_ENGAGEMENT_KINDS = ['clicked', 'applied'] as const;
export type JobAlertEngagementKind = (typeof JOB_ALERT_ENGAGEMENT_KINDS)[number];

/** Mirrors the jobs page's work-mode chips (D1 vocabulary). */
export const JOB_ALERT_WORK_MODES = ['remote', 'onsite', 'hybrid'] as const;
export type JobAlertWorkMode = (typeof JOB_ALERT_WORK_MODES)[number];

export const JOB_ALERT_JOB_TYPES = ['full-time', 'part-time', 'contract', 'internship'] as const;
export type JobAlertJobType = (typeof JOB_ALERT_JOB_TYPES)[number];

/** Mirrors the jobs page experience select (v4-frontend components/jobs/job-filters.tsx). */
export const JOB_ALERT_EXPERIENCE_YEARS = ['0-2', '2-4', '4-6', '6-8', '8-10', '10-15', '15-20', '20+'] as const;
export type JobAlertExperienceYears = (typeof JOB_ALERT_EXPERIENCE_YEARS)[number];

export const JOB_ALERT_LIMITS = {
  /** Free for every candidate; the cap bounds abuse and inbox load, not revenue. */
  maxActivePerCandidate: 5,
  /** One email per candidate per run, across all their due alerts. */
  maxJobsPerDigest: 10,
  /** Rows asked of listPublicJobs per alert per run. */
  matchesPerAlertQuery: 25,
  /** A new alert's first digest looks back this far, so it is never empty on day one. */
  firstRunLookbackDays: 7,
  nameMaxLength: 80,
  /** listPublicJobs caps its LIKE pattern at 48 bytes; a longer search would be silently cut. */
  searchMaxLength: 48,
  locationMaxLength: 60,
  leaseMinutes: 10,
  /** Candidates leased per scheduler tick (every minute) — ~28K digests/day ceiling. */
  candidatesPerTick: 20,
  /** Default global daily ceiling; JOB_ALERTS_DAILY_EMAIL_CAP overrides. */
  dailyEmailCapDefault: 5000,
  /** A `blocked` run retries the candidate's alerts after this many minutes. */
  blockedRetryMinutes: 60,
} as const;

export const JOB_ALERT_TIMEZONE = 'Asia/Kolkata';
/** Digests go out from 09:00 IST (03:30 UTC; India has no DST). */
export const JOB_ALERT_SEND_HOUR_IST = 9;
/** Weekly digests go out on Monday (JS getUTCDay() numbering, evaluated in IST). */
export const JOB_ALERT_WEEKLY_DAY = 1;

export const JOB_ALERT_CONSENT_COPY_VERSION = 'job-alert-consent-v1-2026-09-28';
export const JOB_ALERT_TEMPLATE_VERSION = 'job-alert-digest-v1-2026-09-28';

/**
 * The opt-in sentence shown next to the consent checkbox. `{email}` is the
 * candidate's address. Changing the words means bumping
 * JOB_ALERT_CONSENT_COPY_VERSION, because alert rows record which copy the
 * candidate agreed to.
 */
export const JOB_ALERT_CONSENT_COPY =
  'Email me new jobs that match this search at {email}. I can pause or unsubscribe at any time.';

/** One-click unsubscribe token: 32 random bytes, base64url, no padding (43 chars). Only its SHA-256 is stored. */
export const JOB_ALERT_UNSUBSCRIBE_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

// ---------------------------------------------------------------------------
// Criteria
// ---------------------------------------------------------------------------

/** C0/C1 control characters left after whitespace is collapsed (NUL, BEL, DEL…). */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\u0000-\u001f\u007f-\u009f]/;

// Criteria text is echoed into digest emails and URLs: ordinary whitespace
// (tabs, newlines) collapses to one space; any other control character is refused.
const trimmedText = (max: number) =>
  z
    .string()
    .transform((s) => s.trim().replace(/\s+/g, ' '))
    .pipe(
      z
        .string()
        .min(1)
        .max(max)
        .refine((s) => !CONTROL_CHARS_RE.test(s), 'Control characters are not allowed'),
    );

export const jobAlertCriteriaSchema = z
  .object({
    search: trimmedText(JOB_ALERT_LIMITS.searchMaxLength).optional(),
    location: trimmedText(JOB_ALERT_LIMITS.locationMaxLength).optional(),
    workMode: z
      .array(z.enum(JOB_ALERT_WORK_MODES))
      .min(1)
      .max(JOB_ALERT_WORK_MODES.length)
      .refine((modes) => new Set(modes).size === modes.length, 'workMode must not repeat')
      .optional(),
    jobType: z.enum(JOB_ALERT_JOB_TYPES).optional(),
    experienceYears: z.enum(JOB_ALERT_EXPERIENCE_YEARS).optional(),
  })
  .strict()
  .refine((c) => Boolean(c.search || c.location), {
    message: 'A job alert needs a search or a location',
  });

export type JobAlertCriteria = z.infer<typeof jobAlertCriteriaSchema>;

/**
 * Stable identity of an alert's search, used to refuse a duplicate active
 * alert: case, padding and work-mode order do not make a different alert.
 */
export function jobAlertCriteriaKey(criteria: JobAlertCriteria): string {
  const norm = (s: string | undefined) => (s ? s.trim().replace(/\s+/g, ' ').toLowerCase() : null);
  return JSON.stringify([
    norm(criteria.search),
    norm(criteria.location),
    criteria.workMode ? [...criteria.workMode].sort() : null,
    criteria.jobType ?? null,
    criteria.experienceYears ?? null,
  ]);
}

const WORK_MODE_LABEL: Record<JobAlertWorkMode, string> = { remote: 'Remote', onsite: 'Onsite', hybrid: 'Hybrid' };
const JOB_TYPE_LABEL: Record<JobAlertJobType, string> = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
  contract: 'Contract',
  internship: 'Internship',
};

/** "React · Pune · Remote, Hybrid · Full-time · 2-4 yrs" */
export function summarizeJobAlertCriteria(criteria: JobAlertCriteria): string {
  const parts: string[] = [];
  if (criteria.search) parts.push(criteria.search);
  if (criteria.location) parts.push(criteria.location);
  if (criteria.workMode?.length) parts.push(criteria.workMode.map((m) => WORK_MODE_LABEL[m]).join(', '));
  if (criteria.jobType) parts.push(JOB_TYPE_LABEL[criteria.jobType]);
  if (criteria.experienceYears) parts.push(`${criteria.experienceYears} yrs`);
  return parts.join(' · ');
}

export function defaultJobAlertName(criteria: JobAlertCriteria): string {
  const name = criteria.search && criteria.location
    ? `${criteria.search} in ${criteria.location}`
    : criteria.search
      ? criteria.search
      : `Jobs in ${criteria.location}`;
  return name.length > JOB_ALERT_LIMITS.nameMaxLength ? `${name.slice(0, JOB_ALERT_LIMITS.nameMaxLength - 1)}…` : name;
}

/** The subset of vhire-job's PublicJobFilters a digest query uses. */
export interface JobAlertPublicJobFilters {
  search?: string;
  location?: string;
  workMode?: JobAlertWorkMode[];
  jobType?: JobAlertJobType;
  experienceYears?: string;
  /** Unix seconds. Only jobs with sort_ts strictly greater are returned. */
  postedAfter: number;
  limit: number;
}

export function jobAlertCriteriaToPublicJobFilters(
  criteria: JobAlertCriteria,
  postedAfter: number,
  limit: number,
): JobAlertPublicJobFilters {
  const filters: JobAlertPublicJobFilters = { postedAfter, limit };
  if (criteria.search) filters.search = criteria.search;
  if (criteria.location) filters.location = criteria.location;
  if (criteria.workMode?.length) filters.workMode = [...criteria.workMode];
  if (criteria.jobType) filters.jobType = criteria.jobType;
  if (criteria.experienceYears) filters.experienceYears = criteria.experienceYears;
  return filters;
}

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

const IST_OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Next digest time, strictly after `after`.
 *   daily            → the next 09:00 IST
 *   weekly           → the next Monday 09:00 IST
 *   { first: true }  → the next 09:00 IST whatever the frequency, so a new
 *                      alert proves its value within a day
 */
export function computeNextJobAlertRunAt(
  frequency: JobAlertFrequency,
  after: Date,
  opts: { first?: boolean } = {},
): Date {
  // Shift into IST wall-clock, compute there, shift back.
  const ist = new Date(after.getTime() + IST_OFFSET_MS);
  let candidate = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate(), JOB_ALERT_SEND_HOUR_IST, 0, 0, 0);
  if (candidate <= ist.getTime()) candidate += DAY_MS;
  if (frequency === 'weekly' && !opts.first) {
    while (new Date(candidate).getUTCDay() !== JOB_ALERT_WEEKLY_DAY) candidate += DAY_MS;
  }
  return new Date(candidate - IST_OFFSET_MS);
}

// ---------------------------------------------------------------------------
// URLs (email links and the jobs page share one definition)
// ---------------------------------------------------------------------------

function trimBase(base: string): string {
  return base.replace(/\/+$/, '');
}

/** A job link inside a digest. `ja` attributes the click to its run. */
export function jobAlertJobUrl(appBase: string, slug: string, runId: string, frequency: JobAlertFrequency): string {
  const params = new URLSearchParams({
    utm_source: 'job_alert',
    utm_medium: 'email',
    utm_campaign: frequency,
    ja: runId,
  });
  return `${trimBase(appBase)}/jobs/${encodeURIComponent(slug)}?${params.toString()}`;
}

/** "See all matching jobs" — the jobs page reads these params back with parseJobAlertSearchParams. */
export function jobAlertSeeAllUrl(appBase: string, criteria: JobAlertCriteria, runId?: string): string {
  const params = new URLSearchParams();
  if (criteria.search) params.set('search', criteria.search);
  if (criteria.location) params.set('location', criteria.location);
  if (criteria.workMode?.length) params.set('workMode', criteria.workMode.join(','));
  if (criteria.jobType) params.set('jobType', criteria.jobType);
  if (criteria.experienceYears) params.set('experienceYears', criteria.experienceYears);
  if (runId) {
    params.set('utm_source', 'job_alert');
    params.set('utm_medium', 'email');
    params.set('ja', runId);
  }
  return `${trimBase(appBase)}/jobs?${params.toString()}`;
}

/** Inverse of jobAlertSeeAllUrl. Invalid values are dropped, never guessed. */
export function parseJobAlertSearchParams(params: URLSearchParams): Partial<JobAlertCriteria> {
  const out: Partial<JobAlertCriteria> = {};
  const text = (key: string, max: number) => {
    const raw = params.get(key);
    if (raw == null) return undefined;
    const v = raw.trim().replace(/\s+/g, ' ');
    return v.length > 0 && v.length <= max ? v : undefined;
  };
  const search = text('search', JOB_ALERT_LIMITS.searchMaxLength);
  if (search) out.search = search;
  const location = text('location', JOB_ALERT_LIMITS.locationMaxLength);
  if (location) out.location = location;
  const modes = (params.get('workMode') ?? '')
    .split(',')
    .map((m) => m.trim())
    .filter((m): m is JobAlertWorkMode => (JOB_ALERT_WORK_MODES as readonly string[]).includes(m));
  const uniqueModes = [...new Set(modes)];
  if (uniqueModes.length) out.workMode = uniqueModes;
  const jobType = params.get('jobType');
  if (jobType && (JOB_ALERT_JOB_TYPES as readonly string[]).includes(jobType)) out.jobType = jobType as JobAlertJobType;
  const exp = params.get('experienceYears');
  if (exp && (JOB_ALERT_EXPERIENCE_YEARS as readonly string[]).includes(exp)) out.experienceYears = exp as JobAlertExperienceYears;
  return out;
}

export function jobAlertManageUrl(appBase: string): string {
  return `${trimBase(appBase)}/me/alerts`;
}

export function jobAlertUnsubscribePageUrl(appBase: string, token: string): string {
  return `${trimBase(appBase)}/alerts/unsubscribe/${token}`;
}

/** RFC 8058 List-Unsubscribe-Post target (the gateway's public route). */
export function jobAlertOneClickUnsubscribeUrl(apiBase: string, token: string): string {
  return `${trimBase(apiBase)}/api/v1/job-alerts/unsubscribe/${token}`;
}

// ---------------------------------------------------------------------------
// Results and errors
// ---------------------------------------------------------------------------

export type JobAlertErrorCode =
  | 'INVALID_REQUEST'
  | 'NOT_FOUND'
  | 'ALERT_LIMIT_REACHED'
  | 'DUPLICATE_ALERT'
  | 'CONSENT_REQUIRED'
  | 'NO_EMAIL'
  | 'CANDIDATE_INACTIVE'
  /** Migration 268 is not applied yet — callers hide the feature, they do not error loudly. */
  | 'JOB_ALERTS_UNAVAILABLE'
  | 'INTERNAL_ERROR';

export type JobAlertResult<T> =
  | { success: true; data: T }
  | { success: false; code: JobAlertErrorCode; error: string };

// ---------------------------------------------------------------------------
// Candidate-facing alert model
// ---------------------------------------------------------------------------

export interface JobAlert {
  id: string;
  name: string;
  criteria: JobAlertCriteria;
  summary: string;
  frequency: JobAlertFrequency;
  /** Deleted alerts are never returned. */
  status: Exclude<JobAlertStatus, 'deleted'>;
  pausedReason: JobAlertPauseReason | null;
  channel: 'email';
  source: JobAlertSource;
  createdAt: string;
  nextRunAt: string | null;
  lastSentAt: string | null;
}

export interface JobAlertSuggestion {
  name: string;
  criteria: JobAlertCriteria;
  summary: string;
}

export interface ListJobAlertsRequest {
  candidateId: string;
}

export interface ListJobAlertsResult {
  alerts: JobAlert[];
  /** The address digests go to. The candidate's own email, shown to them only. */
  email: string | null;
  maxActive: number;
  activeCount: number;
  /** Built from the profile (title + preferred location) when the candidate has no alerts yet. */
  suggestion: JobAlertSuggestion | null;
}

export interface JobAlertConsentInput {
  accepted: true;
  copyVersion: string;
  /** Normalized by the gateway; bad values degrade to null. */
  ip?: string | null;
  userAgent?: string | null;
}

export interface CreateJobAlertRequest {
  candidateId: string;
  name?: string;
  criteria: JobAlertCriteria;
  frequency: JobAlertFrequency;
  source: JobAlertSource;
  consent: JobAlertConsentInput;
}

export interface UpdateJobAlertRequest {
  candidateId: string;
  alertId: string;
  name?: string;
  frequency?: JobAlertFrequency;
  /** `active` re-activates (counts against the cap); `paused` pauses with reason `candidate`. */
  status?: 'active' | 'paused';
  /**
   * Required when `status: 'active'` turns an UNSUBSCRIBED alert back on — that
   * is a new opt-in and its evidence replaces the old. Ignored otherwise (a
   * paused alert's consent still stands).
   */
  consent?: JobAlertConsentInput;
}

export interface DeleteJobAlertRequest {
  candidateId: string;
  alertId: string;
}

export interface UnsubscribeJobAlertsRequest {
  token: string;
  via: Exclude<JobAlertUnsubscribeVia, 'manage_page'>;
}

export interface UnsubscribeJobAlertsResult {
  /** Alerts moved to `unsubscribed` by this call. */
  unsubscribed: number;
  /** The token was valid but every alert was already unsubscribed or deleted. */
  alreadyUnsubscribed: boolean;
}

export interface RecordJobAlertEngagementRequest {
  runId: string;
  jobSlug: string;
  kind: JobAlertEngagementKind;
}

export interface RecordJobAlertEngagementResult {
  /** False when the run/slug pair is unknown or already recorded — never an error to the caller. */
  recorded: boolean;
}

export interface RunJobAlertDigestNowRequest {
  candidateId: string;
}

export interface JobAlertDigestTickResult {
  leasedCandidates: number;
  emailsSent: number;
  emailsUncertain: number;
  emailsFailed: number;
  emailsBlocked: number;
  /** Candidates with due alerts but no new matching jobs — no email, schedule advanced. */
  noNewJobs: number;
  skipped: number;
}

// ---------------------------------------------------------------------------
// vhire-email: sendJobAlertDigest
// ---------------------------------------------------------------------------

export interface JobAlertDigestJob {
  jobId: string;
  slug: string;
  title: string;
  /** Null when the employer hides the client name. */
  company: string | null;
  location: string | null;
  workMode: JobAlertWorkMode | null;
  /** Pre-formatted, e.g. "2–5 yrs". */
  experience: string | null;
  /** Pre-formatted, e.g. "₹12–18 LPA"; null when hidden or unknown. */
  salary: string | null;
}

export interface JobAlertDigestSection {
  alertId: string;
  alertName: string;
  summary: string;
  criteria: JobAlertCriteria;
  jobs: JobAlertDigestJob[];
}

export interface SendJobAlertDigestRequest {
  runId: string;
  templateVersion: string;
  frequency: JobAlertFrequency;
  to: { email: string; firstName: string | null };
  sections: JobAlertDigestSection[];
  totalJobs: number;
  /** Plaintext; only its SHA-256 is stored on the run row. */
  unsubscribeToken: string;
  /** When the candidate's resume ATS score is known and below 70, one secondary line invites them to see what to fix. */
  atsNudge: { score: number } | null;
  /** ISO date the oldest included alert was created — the "why am I getting this" footer. */
  subscribedSince: string;
}

export type JobAlertDigestSendOutcome =
  | { status: 'sent'; providerMessageId: string }
  | { status: 'uncertain'; reason: string }
  | { status: 'failed'; reason: string; invalidAddress?: boolean }
  | { status: 'blocked'; reason: string };

// ---------------------------------------------------------------------------
// vhire-candidate RPC surface (CandidateService default entrypoint)
// ---------------------------------------------------------------------------

export interface JobAlertsCandidateRpc {
  listJobAlerts(req: ListJobAlertsRequest): Promise<JobAlertResult<ListJobAlertsResult>>;
  createJobAlert(req: CreateJobAlertRequest): Promise<JobAlertResult<{ alert: JobAlert }>>;
  updateJobAlert(req: UpdateJobAlertRequest): Promise<JobAlertResult<{ alert: JobAlert }>>;
  deleteJobAlert(req: DeleteJobAlertRequest): Promise<JobAlertResult<{ deleted: true }>>;
  unsubscribeJobAlertsByToken(req: UnsubscribeJobAlertsRequest): Promise<JobAlertResult<UnsubscribeJobAlertsResult>>;
  recordJobAlertEngagement(req: RecordJobAlertEngagementRequest): Promise<JobAlertResult<RecordJobAlertEngagementResult>>;
  runJobAlertDigestNow(req: RunJobAlertDigestNowRequest): Promise<JobAlertResult<JobAlertDigestTickResult>>;
  getCandidateEngagementReport(req: GetCandidateEngagementReportRequest): Promise<JobAlertResult<CandidateEngagementReport>>;
}

export interface JobAlertEmailRpc {
  sendJobAlertDigest(req: SendJobAlertDigestRequest): Promise<JobAlertDigestSendOutcome>;
}

// ---------------------------------------------------------------------------
// Candidate engagement report (platform-admin dashboard)
// ---------------------------------------------------------------------------

export const CANDIDATE_ENGAGEMENT_WINDOWS = [7, 30, 90] as const;
export type CandidateEngagementWindow = (typeof CANDIDATE_ENGAGEMENT_WINDOWS)[number];

export type CandidateEngagementGateKey =
  | 'delivered_to_claim'
  | 'claim_to_profile'
  | 'claim_to_ats'
  | 'claimed_7d_return'
  | 'ats_to_upgrade_intent';

export interface CandidateEngagementGateDefinition {
  key: CandidateEngagementGateKey;
  label: string;
  numeratorLabel: string;
  denominatorLabel: string;
  /** Passes when rate is strictly greater than this. */
  threshold: number;
  /** Below this denominator the gate shows "not enough data" instead of a verdict. */
  minDenominator: number;
}

/** Gate A — CANDIDATE_MONETIZATION_PLAN_2026-08-30.md §3. */
export const CANDIDATE_ENGAGEMENT_GATES: readonly CandidateEngagementGateDefinition[] = [
  { key: 'delivered_to_claim', label: 'Email delivered → claim', numeratorLabel: 'claimed', denominatorLabel: 'invites delivered', threshold: 0.03, minDenominator: 500 },
  { key: 'claim_to_profile', label: 'Claim → profile visit', numeratorLabel: 'visited profile', denominatorLabel: 'claimed', threshold: 0.6, minDenominator: 30 },
  { key: 'claim_to_ats', label: 'Claim → ATS check viewed', numeratorLabel: 'viewed ATS check', denominatorLabel: 'claimed', threshold: 0.3, minDenominator: 30 },
  { key: 'claimed_7d_return', label: 'Claimed 7-day return', numeratorLabel: 'returned within 7 days', denominatorLabel: 'claimed 7+ days ago', threshold: 0.15, minDenominator: 30 },
  { key: 'ats_to_upgrade_intent', label: 'ATS check → upgrade intent', numeratorLabel: 'clicked upgrade', denominatorLabel: 'viewed ATS check', threshold: 0.05, minDenominator: 30 },
];

export type CandidateEngagementGateStatus = 'pass' | 'fail' | 'insufficient_data';

export function evaluateEngagementGate(
  gate: CandidateEngagementGateDefinition,
  numerator: number,
  denominator: number,
): { rate: number | null; status: CandidateEngagementGateStatus } {
  if (denominator <= 0) return { rate: null, status: 'insufficient_data' };
  const rate = numerator / denominator;
  if (denominator < gate.minDenominator) return { rate, status: 'insufficient_data' };
  return { rate, status: rate > gate.threshold ? 'pass' : 'fail' };
}

export interface GetCandidateEngagementReportRequest {
  days: CandidateEngagementWindow;
}

export interface CandidateEngagementGateResult {
  key: CandidateEngagementGateKey;
  label: string;
  numerator: number;
  denominator: number;
  rate: number | null;
  threshold: number;
  status: CandidateEngagementGateStatus;
}

export interface CandidateEngagementDailyPoint {
  /** YYYY-MM-DD in IST. */
  date: string;
  claims: number;
  profileVisits: number;
  alertsCreated: number;
  digestsSent: number;
  alertClicks: number;
  alertApplies: number;
  proOrdersPaid: number;
}

export interface CandidateEngagementReport {
  generatedAt: string;
  windowDays: CandidateEngagementWindow;
  windowStart: string;
  /** The inventory recruiter-side pricing is built on (monetization plan §5). */
  inventory: {
    claimedTotal: number;
    active7d: number;
    active30d: number;
    claimedActive30d: number;
  };
  claimFunnel: {
    invitesSent: number;
    invitesDelivered: number;
    /** Every claim in the window, invited or organic. */
    claimed: number;
    /** Claims among the invites delivered in the window — the delivered→claim gate's numerator. */
    deliveredClaimed: number;
    profileVisited: number;
    atsViewed: number;
    returned7d: number;
    returned7dEligible: number;
    atsUpgradeClicks: number;
  };
  gateA: CandidateEngagementGateResult[];
  jobAlerts: {
    candidatesWithActiveAlerts: number;
    activeAlerts: number;
    alertsCreated: number;
    alertsCreatedBySource: Record<JobAlertSource, number>;
    /** Runs the provider accepted (status `sent`) — the click-rate denominator. */
    digestsSent: number;
    /** Runs that may or may not have been delivered (never retried); shown so a transport problem is visible. */
    digestsUncertain: number;
    digestsFailed: number;
    jobsDelivered: number;
    digestsClicked: number;
    clicks: number;
    applies: number;
    /** Distinct candidates who unsubscribed (one-click turns off all of a candidate's alerts at once). */
    unsubscribes: number;
    /** digestsClicked / digestsSent */
    clickRate: number | null;
    /** applies / clicks */
    applyRate: number | null;
  };
  monetization: {
    proOrdersPaid: number;
    revenuePaise: number;
    activeProEntitlements: number;
  };
  daily: CandidateEngagementDailyPoint[];
}
